import { OffboardingInitiatedBy } from "@/modules/offboarding/enums"
import { useState, useEffect } from "react"
import { User, Calendar, MessageSquare, Pencil, Save, X, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { Card, CardContent } from "@/modules/shared/ui/components/card"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Button } from "@/modules/shared/ui/components/button"
import { Input } from "@/modules/shared/ui/components/input"
import { Textarea } from "@/modules/shared/ui/components/textarea"
import { DatePicker } from "@/modules/shared/ui/components/date-picker"
import { offboardingService } from "@/modules/offboarding/infrastructure/offboarding-service"

interface Props {
  offboardingId: string
  organizationId: string
  offboardingReason: string
  lastWorkingDate: string
  initiatedBy: string
  comments?: string | null
  isTerminal: boolean
  onRefresh: () => void
}

function startOfToday() {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-border last:border-0">
      <div className="text-muted-foreground mt-0.5 shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</p>
        <div className="text-sm text-foreground mt-0.5">{value}</div>
      </div>
    </div>
  )
}

export function OffboardingInfoCard({
  offboardingId,
  organizationId,
  offboardingReason,
  lastWorkingDate,
  initiatedBy,
  comments,
  isTerminal,
  onRefresh,
}: Props) {
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [editForm, setEditForm] = useState({
    offboardingReason: "",
    lastWorkingDate: undefined as Date | undefined,
    comments: "",
  })

  useEffect(() => {
    setEditForm({
      offboardingReason,
      lastWorkingDate: lastWorkingDate ? new Date(lastWorkingDate) : undefined,
      comments: comments ?? "",
    })
  }, [offboardingReason, lastWorkingDate, comments])

  const isLastWorkingDateValid = !editForm.lastWorkingDate || editForm.lastWorkingDate >= startOfToday()

  const handleSave = async () => {
    if (!isLastWorkingDateValid) {
      toast.error("Last working date cannot be in the past")
      return
    }
    const payload = {
      offboardingReason: editForm.offboardingReason,
      lastWorkingDate: editForm.lastWorkingDate?.toISOString(),
      ...(editForm.comments && { comments: editForm.comments }),
    }
    setIsSaving(true)
    try {
      await offboardingService.patch(organizationId, offboardingId, payload)
      toast.success("Offboarding updated")
      setIsEditing(false)
      onRefresh()
    } catch {
      toast.error("Failed to update offboarding")
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Card className="bg-card border border-border">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-foreground">Offboarding Information</h2>
          {!isTerminal && (!isEditing ? (
            <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>
              <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setIsEditing(false)} disabled={isSaving}>
                <X className="w-3.5 h-3.5 mr-1" /> Cancel
              </Button>
              <Button size="sm" className="bg-orange-600 hover:bg-orange-700 text-white border-0" onClick={handleSave} disabled={isSaving || !isLastWorkingDateValid}>
                {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" />Save</>}
              </Button>
            </div>
          ))}
        </div>
        {!isEditing ? (
          <>
            <InfoRow icon={<User className="w-4 h-4" />} label="Reason" value={offboardingReason} />
            <InfoRow icon={<Calendar className="w-4 h-4" />} label="Last Working Date"
              value={new Date(lastWorkingDate).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} />
            <InfoRow icon={<User className="w-4 h-4" />} label="Initiated By"
              value={<Badge className={initiatedBy?.toLowerCase() === OffboardingInitiatedBy.Admin.toLowerCase() ? "bg-purple-100 text-purple-700 border-purple-200" : "bg-blue-100 text-blue-700 border-blue-200"}>{initiatedBy}</Badge>} />
            {comments && (
              <InfoRow icon={<MessageSquare className="w-4 h-4" />} label="Comments"
                value={<span className="italic text-muted-foreground">"{comments}"</span>} />
            )}
          </>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Reason</label>
              <Input className="mt-1" value={editForm.offboardingReason} onChange={(e) => setEditForm((f) => ({ ...f, offboardingReason: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Last Working Date</label>
              <div className="mt-1">
                <DatePicker
                  value={editForm.lastWorkingDate}
                  onChange={(d) => setEditForm((f) => ({ ...f, lastWorkingDate: d }))}
                  placeholder="Pick last working date"
                  disabled={{ before: startOfToday() }}
                />
              </div>
              {!isLastWorkingDateValid && (
                <p className="text-xs text-destructive mt-1">Last working date cannot be in the past</p>
              )}
            </div>
            <div>
              <label className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Comments</label>
              <Textarea className="mt-1 resize-none" rows={3} value={editForm.comments}
                onChange={(e) => setEditForm((f) => ({ ...f, comments: e.target.value }))} placeholder="Optional comments" />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
